import {
  InvitationWrapper,
  ModalTitle,
  ExternalRegisterButton,
  StackItems,
  FormSeparator,
} from "./Styles";

import { Modal } from "../../Components/Modal";
import InvitationVector from "../../Assets/Vectors/invitation.png";
import { Button } from "../../Components/Button";
import { Input } from "../../Components/Input";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaRegUserCircle, FaLinkedin } from "react-icons/fa";
import { useAuth } from "../../Common/Hooks/useAuth";
import { TbLock } from "react-icons/tb";
import { eventBus } from "../../Common/EventBus";
import { useRecoilState } from "recoil";
import { Notification } from "../../Components/Notifications";
import { invitationState } from "../../Common/State/Invitation";
import { isErr } from "../../Common/Utils/Result";
import {
  Invitation,
  ClientUserInvitationStatus,
} from "../../Common/Types-DTOs/Invitation";

export const InvitationPage = () => {
  const { t } = useTranslation();
  const [invitationSso, setInvitationSso] = useRecoilState(invitationState);
  const [_, setInvitationId] = useState<string>("");

  const [step, setStep] = useState<number>(1);
  const [invitation, setInvitation] = useState<Invitation | undefined>(
    undefined
  );
  const { useInvitationWithSso, getInvitation } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invitationIdParam = queryParams.get("invitationId");

    if (!invitationIdParam) {
      navigate("/login");
      return;
    }

    setInvitationId(invitationIdParam);

    getInvitation(invitationIdParam).then((result) => {
      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            t("common.invitation.errors.fetchFailed")
          )
        );
        return;
      }

      if (result.value.status != ClientUserInvitationStatus.PENDING) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            result.value.status == ClientUserInvitationStatus.USED
              ? t("common.invitation.errors.alreadyUsed")
              : t("common.invitation.errors.expired")
          )
        );
        navigate("/login");
        return;
      }

      setInvitation(result.value);
    });
  }, [t]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invitationIdParam = queryParams.get("invitationId");

    if (
      invitationSso.ssoProvider &&
      invitationSso.ssoToken &&
      invitationIdParam
    ) {
      useInvitationWithSso({
        invitationId: invitationIdParam,
        tokenSso: invitationSso.ssoToken,
        ssoProvider: invitationSso.ssoProvider,
      }).then(async (result) => {
        if (isErr(result)) {
          eventBus.emit(
            "NOTIFICATION",
            Notification.buildNotification("error", t("common.invitation.errors.registerFailed"))
          );
          return;
        }

        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("success", t("common.invitation.success.registered"))
        );

        setInvitationSso({ ssoProvider: undefined, ssoToken: undefined });

        navigate("/chat");
      });
    }
  }, [invitationSso.ssoProvider, invitationSso.ssoToken, t]);

  return (
    <Modal isOpen>
      <InvitationWrapper>
        {step === 1 && (
          <Step1
            setStep={(n) => setStep(n)}
            businessName={invitation ? invitation.businessName : undefined}
          />
        )}
        {step === 2 && <Step2 invitation={invitation!} />}
      </InvitationWrapper>
    </Modal>
  );
};

const Step1 = ({
  setStep,
  businessName,
}: {
  setStep: (step: number) => void;
  businessName: string | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <img src={InvitationVector} alt={t("common.invitation.imageAlt")} />
      <ModalTitle>
        {t("common.invitation.inviteTitlePrefix")} <span>{businessName}</span>
      </ModalTitle>
      <Button onClick={() => setStep(2)}>{t("common.invitation.createAccountButton")}</Button>
    </>
  );
};

const Step2 = ({ invitation }: { invitation: Invitation }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [_, setInvitation] = useRecoilState(invitationState);
  const {
    preRegisterUserWithGoogleUsingInvitation,
    preRegisterUserWithLinkedinUsingInvitation,
    useInvitation,
  } = useAuth();
  const navigate = useNavigate();

  const onRegister = async () => {
    if (!password || !confirmPassword) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t("common.invitation.errors.missingFields")
        )
      );
      return;
    }

    if (password !== confirmPassword) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.invitation.errors.passwordsDontMatch"))
      );
      return;
    }

    if (!invitation.email) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.invitation.errors.invalidInvitation"))
      );
      return;
    }

    const result = await useInvitation({
      invitationId: invitation.id,
      email: invitation.email,
      password,
    });

    console.log(result);

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("common.invitation.errors.registerFailed"))
      );
      return;
    }

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification("success", t("common.invitation.success.registered"))
    );

    setInvitation({ ssoProvider: undefined, ssoToken: undefined });

    navigate("/chat");
  };

  const registerWithGoogle = async () => {
    preRegisterUserWithGoogleUsingInvitation(invitation.id);
  };

  const registerWithLinkedIn = async () => {
    preRegisterUserWithLinkedinUsingInvitation(invitation.id);
  };

  return (
    <>
      <ModalTitle>{t("common.invitation.createUserTitle")}</ModalTitle>
      <StackItems>
        <ExternalRegisterButton onClick={registerWithGoogle}>
          <FcGoogle /> {t("common.invitation.registerWithGoogleButton")}
        </ExternalRegisterButton>
        <ExternalRegisterButton onClick={registerWithLinkedIn}>
          <FaLinkedin /> {t("common.invitation.registerWithLinkedinButton")}
        </ExternalRegisterButton>
      </StackItems>
      <FormSeparator>{t("common.invitation.orSeparator")}</FormSeparator>
      <StackItems>
        <Input
          defaultValue={invitation.email}
          Icon={FaRegUserCircle}
          placeholder={invitation.email}
          type="text"
          value={invitation.email}
          disabled={true}
        />
        <Input
          Icon={TbLock}
          placeholder={t("common.invitation.passwordPlaceholder")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          Icon={TbLock}
          placeholder={t("common.invitation.confirmPasswordPlaceholder")}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </StackItems>
      <Button onClick={onRegister}>{t("common.invitation.createAccountButton")}</Button>
    </>
  );
};
